// src/store.js — App State, Settings & Persistence
// Extracted from main.js for modularity
import { pushMatchToCloud } from './services/cloud-sync.js';
import { isNativeApp } from './platform.js';
import { PROD_BASE_URL } from './app-config.js';
import { saveJsonToDevice } from './platform-save.js';
import { toast, alertDialog, confirmDialog } from './ui/dialogs.js';
import QRCode from 'qrcode';

import { validateMatchSnapshot, normalizeSnapshot } from './match-schema.js';
import { normalizeImportedMatch } from './match-import.js';
import { teams } from './teams-data.js';
import { get } from 'svelte/store';
import { activeMatch, recoveryInfo, savedMatches } from './svelte/store.js';
import { sendCurrentMatchState } from './sync/state-publisher.js';
import {
  LAST_RECOVERY_MATCH_KEY,
  clearRecoveryCheckpoints,
  idbPut,
  readRecoveryCheckpoints,
  restoreCheckpoint,
  selectRecoveryState,
  writeRecoveryCheckpoint,
  writeRecoveryCheckpointList
} from './match-recovery.js';
import { stripLocalDeviceSession } from './device-session.js';
import { deleteActiveMatchRecords } from './session-storage.js';
import { cloneMatchForLocalSession } from './session-snapshot.js';
import { prepareActiveSnapshot, selectNewestActiveSnapshot } from './active-session.js';
import {
  applyRecordingPolicyMigration,
  getRecommendedVideoBitrate,
  SAFE_RECORDING_PROFILE
} from './media/recording-policy.js';

// State has been removed and migrated to Svelte stores!

// ─── SETTINGS (persisted across sessions) ───────────────────────────────
const LS_SETTINGS = "cs_settings_v1";
const SETTINGS_SCHEMA_VERSION = 14;
const DEFAULT_SETTINGS = {
  resolution: "1080p",           // 1080p default (safer for battery/thermal). Users can opt up to 1440p/4K in Settings.
  cameraDeviceId: "",            // "" = auto (back camera). Else exact getUserMedia deviceId — USB webcam / capture card (desktop), or a specific phone lens.
  recordingLayout: "landscape",  // "landscape" (16:9) or "vertical" (9:16)
  fps: 30,
  micGain: 2.5,
  videoBitrate: SAFE_RECORDING_PROFILE.videoBitrate,
  sfxEnabled: true,              // Stadium SFX enabled for 4, 6, Wicket, Fanfare
  voiceCommentary: false,        // New: Scorer Voice Commentary
  scorerMicGain: 2.0,            // New: Scorer Mic Gain
  colorBoost: true,              // subtle saturation/contrast lift for that "broadcast" look
  audioBitrate: SAFE_RECORDING_PROFILE.audioBitrate,
  venue: "Rohini Sec 28",        // shown on the burnt-in MATCH_INTRO card. Editable in Settings.
  confirmNewMatch: true,
  showOverballStats: true,
  enableShotSelector: false,
  eventAnimationsEnabled: true,
  autoBroadcastMoments: true,
  stableRecordingProtection: true,
  workerOpfsWriter: false,       // Internal R1 feature flag; enable only after target-WebView qualification.
  nativeRecordingGuard: false,   // Internal R1 feature flag; enable only after Android device qualification.
  directGalleryWriter: false,    // Internal R2 feature flag; keep OFF until physical Android qualification.
  directGalleryQualificationMirror: false, // Internal R2 dual-write qualification only.
  realMp4Writer: false,
  realMp4QualificationMirror: false,
  recordingTargetMinutes: 120,   // Capacity target; emergency stop only occurs near the protected reserve.
  autoMatchIntro: true,          // play INTRO → TOSS → PLAYING XI → STRIKER → NON-STRIKER → BOWLER on first REC
  enablePlayerCards: true,       // show lower third player card on new batsman/bowler
  enableTextCommentary: true,    // show AI commentary ticker on scoring screen
  enableAutoHighlights: false,   // save a .json file with 4s, 6s, and Wickets timestamps alongside the video
  
  // ── GRANULAR SETTINGS ──
  aiCommentaryAudioEnabled: true,
  sfxHornEnabled: true,
  sfxWicketEnabled: true,
  sfxBoundaryEnabled: true,
  animParticlesEnabled: true,
  animTextBannersEnabled: true,
  enableCloudSync: true,
  showAdvancedAnalytics: true,
  enableMatchPhases: true,

  // ── PRO overlay customization ──
  overlayStyle: "classic",       // classic | minimal | professional | vibrant | night
  overlayTitle: "FFPL S3",       // ticker text burnt into the broadcast bar
  overlayLogo: ""                // "" = default FFPL watermark, "off" = hidden, else data-URL of custom logo
};

// Map resolution preset → {width, height, recommendedBitrate}. Used by camera
// constraints, canvas backing-store sizing, and the bitrate hint in Settings.
const RESOLUTION_PRESETS = {
  "720p":  { w: 1280, h: 720,  label: "HD 720p",        recBitrate30: getRecommendedVideoBitrate("720p", 30),  recBitrate60: getRecommendedVideoBitrate("720p", 60) },
  "1080p": { w: 1920, h: 1080, label: "Full HD 1080p",  recBitrate30: getRecommendedVideoBitrate("1080p", 30), recBitrate60: getRecommendedVideoBitrate("1080p", 60) },
  "1440p": { w: 2560, h: 1440, label: "QHD 1440p",      recBitrate30: getRecommendedVideoBitrate("1440p", 30), recBitrate60: getRecommendedVideoBitrate("1440p", 60) },
  "4k":    { w: 3840, h: 2160, label: "Ultra HD 4K (High Bitrate)", recBitrate30: getRecommendedVideoBitrate("4k", 30), recBitrate60: getRecommendedVideoBitrate("4k", 60) }
};

// Ordered from highest to lowest for progressive fallback
const RESOLUTION_FALLBACK_ORDER = ["4k", "1440p", "1080p", "720p"];

function getResolutionPreset() {
  const preset = RESOLUTION_PRESETS[appSettings.resolution] || RESOLUTION_PRESETS["1080p"];
  if (appSettings.recordingLayout === "vertical") {
    return { ...preset, w: preset.h, h: preset.w };
  }
  return preset;
}
let appSettings = Object.assign({}, DEFAULT_SETTINGS, (() => {
  try { return JSON.parse(localStorage.getItem(LS_SETTINGS) || "{}"); } catch (_) { return {}; }
})());
function saveSettings() {
  try { localStorage.setItem(LS_SETTINGS, JSON.stringify(appSettings)); } catch (_) {}
}

// Non-destructive settings migration: preserve existing user choices and only
// fill newly-added defaults / remove dead keys.
let settingsChanged = false;
if (appSettings._settingsSchemaVersion !== SETTINGS_SCHEMA_VERSION) {
  appSettings = Object.assign({}, DEFAULT_SETTINGS, appSettings);
  if (Number(appSettings.fps) < 30) appSettings.fps = 30;
  delete appSettings.autoSplitOnOver;
  delete appSettings.autoReplay;
  delete appSettings.replayDuration;
  delete appSettings._maxQualityMigrated;
  // v5: force-push the requested changes to existing installs — SFX removed,
  // recording quality set to 2K (1440p).
  appSettings._settingsSchemaVersion = SETTINGS_SCHEMA_VERSION;
  settingsChanged = true;
}
const recordingPolicyMigration = applyRecordingPolicyMigration(appSettings);
if (recordingPolicyMigration.changed) appSettings = recordingPolicyMigration.settings;
if (settingsChanged || recordingPolicyMigration.changed) saveSettings();

const LS_ACTIVE = "cs_active_v1";
const LS_SAVED  = "cs_saved_v1";
const LS_TEAMS  = "cs_team_meta_v1";   // colors/logos keyed by team name

// Team meta (colors / logos) — separate from match snapshot so we don't bloat saved JSON
let teamMeta = {};
try { teamMeta = JSON.parse(localStorage.getItem(LS_TEAMS) || "{}"); } catch (_) { teamMeta = {}; }

function saveTeamMeta() {
  try {
    localStorage.setItem(LS_TEAMS, JSON.stringify(teamMeta));
  } catch (e) {
    console.warn("Team meta save failed (quota); dropping logos and retrying", e);
    Object.keys(teamMeta).forEach(k => { if (teamMeta[k]) teamMeta[k].logo = null; });
    try { localStorage.setItem(LS_TEAMS, JSON.stringify(teamMeta)); } catch (_) {}
  }
}
function _findFFPL(name) { return (teams || []).find(t => t.name === name) || null; }
// getMeta resolves color/logo at runtime: prefers stored override, falls back to FFPL pre-loaded teams.
function getMeta(name) {
  const stored = teamMeta[name] || {};
  const ffpl = _findFFPL(name);
  return {
    color: stored.color || (ffpl && ffpl.color) || "#0a7d28",
    logo:  stored.logo  || (ffpl && ffpl.logo)  || null
  };
}
// setMeta only persists OVERRIDES that differ from FFPL defaults — keeps localStorage tiny.
function setMeta(name, patch) {
  const ffpl = _findFFPL(name);
  const next = Object.assign({}, teamMeta[name] || {});
  if (patch.color != null && (!ffpl || patch.color !== ffpl.color)) next.color = patch.color;
  // If logo matches FFPL default, don't store it (saves localStorage). Custom-uploaded logos are kept.
  if (patch.logo != null && (!ffpl || patch.logo !== ffpl.logo)) next.logo = patch.logo;
  teamMeta[name] = next;
  saveTeamMeta();
}

// Resize an image data URL to fit within maxSize x maxSize, returning a small PNG data URL.
function resizeImageDataUrl(dataUrl, maxSize) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const ratio = Math.min(1, maxSize / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * ratio));
      const h = Math.max(1, Math.round(img.height * ratio));
      const c = document.createElement("canvas");
      c.width = w; c.height = h;
      const ctx = c.getContext("2d");
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, 0, 0, w, h);
      // PNG keeps transparency for logos; for very large source use JPEG quality 0.85
      let out;
      try { out = c.toDataURL("image/png"); } catch (e) { out = dataUrl; }
      // If still too large, try JPEG
      if (out.length > 80_000) {
        try { out = c.toDataURL("image/jpeg", 0.82); } catch (e) {}
      }
      resolve(out);
    };
    img.onerror = reject;
    img.src = dataUrl;
  });
}

// ─── MATCH TRANSFER (file export/import + cloud sync) ───────────────────
// Save `data` as pretty-printed JSON — browser download on web, Filesystem +
// share sheet inside the Android app (blob downloads don't work in WebViews).
function _downloadJson(filename, data) {
  saveJsonToDevice(data, filename.replace(/\s+/g, "_")).then(result => {
    if (!result.ok) toast("Could not save the file: " + (result.error?.message || "unknown error"), "error");
  });
}

function _exportStamp() {
  return new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
}

// Export the current match as a downloadable JSON file. 100% reliable —
// no internet needed. Other phone uses "Import from File" to resume.
function exportMatchToFile(match) {
  try {
    const clone = stripLocalDeviceSession(match);
    const nameA = (match.teamA && match.teamA.name) || "A";
    const nameB = (match.teamB && match.teamB.name) || "B";
    _downloadJson(`cricket_match_${nameA}_vs_${nameB}_${_exportStamp()}.json`, clone);
  } catch (e) {
    toast("Export failed: " + (e.message || e), "error");
  }
}

function importMatchFromFile() {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "application/json,.json";
  input.onchange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      // Sanity check — must look like a match object.
      const checked = normalizeImportedMatch(data);
      if (!checked.ok) {
        await alertDialog("That file doesn't look like a Cricket Scorer match file: " + checked.error, { title: "Import failed" });
        return;
      }
      if (get(activeMatch) && !(await confirmDialog("This will replace the current active match. Continue?", { title: "Import match", danger: true }))) return;
      activeMatch.set(checked.value);
      persist();
      toast("✅ Match imported — resume scoring and recording from where the other phone left off.", "success", 4500);
    } catch (err) {
      toast("Couldn't read the file: " + (err.message || err), "error");
    }
  };
  input.click();
}

// Cloud sync — POST to jsonbin. Returns the bin code on success, null on failure.
// We don't update an existing bin (jsonbin v3 needs an API key for PUT and we want
// the app to work fully anonymous). Instead each sync creates a fresh bin and we
// stash its code in appSettings so the home screen can display the latest one.
function getCloudSyncEndpoint() {
  const raw = (typeof globalThis !== "undefined" && globalThis.CS_CLOUD_ENDPOINT) || "";
  if (raw) return String(raw).replace(/\/+$/, "");
  // The native app's WebView origin is https://localhost — relative /matches
  // URLs would resolve against the app shell and fail. Use the deployed
  // production URL instead (see src/app-config.js).
  if (isNativeApp) return String(PROD_BASE_URL || "").replace(/\/+$/, "");
  return "";
}

function isCloudSyncAvailable() {
  if (getCloudSyncEndpoint()) return true;
  if (isNativeApp) return false; // native needs the absolute endpoint above
  if (typeof window === "undefined" || !window.location) return false;
  return !["localhost", "127.0.0.1", "::1"].includes(window.location.hostname);
}

// Cheap content fingerprint — changes whenever the scoring state advances. Used to
// skip redundant cloud writes (e.g. 60s heartbeats during a drinks break) so we don't
// burn Cloudflare KV write quota when nothing actually changed.
function _matchSyncSignature(m) {
  try {
    const inn = m.innings || [];
    const parts = [m.id, m.result ? "R" : "L", inn.length];
    for (const i of inn) parts.push(i.totalRuns, i.wickets, i.balls, (i.ballLog || []).length);
    return parts.join("|");
  } catch (_) { return "sig-" + (m && m.id); }
}

// Publish the match to the KV sync API (Cloudflare Pages /matches). This is
// what powers the 6-char resume codes, the QR links AND the public live-score
// page — it must work even when the user is not logged in. Firestore backup
// (per-user, login required) runs alongside it.
async function _publishMatchToKV(match) {
  const endpoint = getCloudSyncEndpoint();
  if (!isCloudSyncAvailable()) return null;
  const sig = _matchSyncSignature(match);
  if (match._cloudSig === sig && match._cloudCode) return match._cloudCode; // nothing changed
  // Strip local session AND team logos: base64 logos can blow through the
  // API's 200 KB payload limit, and every device resolves logos locally by
  // team name anyway (getMeta).
  const payload = stripLocalDeviceSession(match);
  delete payload._cloudCode;
  delete payload._cloudSig;
  delete payload._cloudEditToken;
  if (payload.teamA) payload.teamA = { ...payload.teamA, logo: null };
  if (payload.teamB) payload.teamB = { ...payload.teamB, logo: null };
  const qs = match._cloudCode ? "?code=" + encodeURIComponent(match._cloudCode) : "";
  const headers = { "content-type": "application/json" };
  if (match._cloudEditToken) headers["x-match-edit-token"] = match._cloudEditToken;
  const res = await fetch(endpoint + "/matches" + qs, {
    method: "POST",
    headers,
    body: JSON.stringify(payload)
  });
  if (!res.ok) {
    // A stale/foreign code can 403 (edit token mismatch) — drop it and let the
    // next sync mint a fresh code instead of failing forever.
    if (res.status === 403) {
      match._cloudCode = null;
      match._cloudEditToken = null;
      match._cloudSig = null;
    }
    return null;
  }
  const json = await res.json();
  if (json.code) match._cloudCode = json.code;
  if (json.editToken) match._cloudEditToken = json.editToken;
  match._cloudSig = sig;
  return json.code || match._cloudCode || null;
}

async function cloudSyncMatch(match) {
  let code = null;
  try {
    code = await _publishMatchToKV(match);
  } catch (e) {
    console.warn("[CloudSync] KV publish failed:", e);
  }
  // Firestore backup for logged-in users (no-op for guests)
  const firebaseOk = await pushMatchToCloud(match);
  if (code || firebaseOk) {
    appSettings._lastSyncAt = Date.now();
    saveSettings();
  }
  return code; // 6-char resume/live code, or null if KV publish unavailable
}

async function cloudResumeMatch(code) {
  const endpoint = getCloudSyncEndpoint();
  const res = await fetch(endpoint + "/matches/" + encodeURIComponent(code), { cache: "no-store" });
  if (!res.ok) return null;
  const json = await res.json();
  const raw = json.record || json.match || json;
  const checked = validateMatchSnapshot(normalizeSnapshot(raw));
  if (!checked.ok) return null;
  delete checked.value._cloudCode;
  delete checked.value._cloudSig;
  delete checked.value._cloudEditToken;
  return checked.value;
}

// Debounced auto-sync to cloud after each scoring event. The user always has the
// freshest code on the Home screen — if the phone dies mid-match, the other
// phone can resume from this code.
let _autoSyncTimer = null;
let _heartbeatSyncTimer = null;
function scheduleAutoCloudSync() {
  if (!isCloudSyncAvailable()) return;
  if (!get(activeMatch)) return;
  if (_autoSyncTimer) clearTimeout(_autoSyncTimer);
  _autoSyncTimer = setTimeout(() => { _autoSyncTimer = null; cloudSyncMatch(get(activeMatch)); }, 2500);
}

// Heartbeat sync — even if no balls are scored for a while (e.g., long innings break,
// drinks break, batter taking guard), we re-sync every 60 seconds so the other phone
// always sees a recent code if a transfer is needed. Safety net for "phone dying" case.
function startHeartbeatSync() {
  stopHeartbeatSync();
  if (!isCloudSyncAvailable()) return;
  _heartbeatSyncTimer = setInterval(() => {
    if (get(activeMatch)) cloudSyncMatch(get(activeMatch));
  }, 60_000);
}
function stopHeartbeatSync() {
  if (_heartbeatSyncTimer) { clearInterval(_heartbeatSyncTimer); _heartbeatSyncTimer = null; }
}

// Public base URL for shareable links. Inside the APK window.location is
// https://localhost — useless for links sent to other people — so use the
// deployed production URL there.
function _publicBaseUrl() {
  if (isNativeApp) return String(PROD_BASE_URL || "").replace(/\/+$/, "");
  return window.location.href.split("?")[0].split("#")[0].replace(/index\.html$/, "").replace(/\/+$/, "");
}

// Build a fully-qualified "resume" URL the other phone can open to auto-load
// the match. URL format: <app-url>?resume=<code>. Lives in QR code too.
function buildResumeUrl(code) {
  if (!code) return null;
  const base = _publicBaseUrl();
  if (!base) return null;
  return base + "/?resume=" + encodeURIComponent(code);
}

// Public live-score page for spectators (no app install needed).
function buildLiveUrl(code) {
  if (!code) return null;
  const base = _publicBaseUrl();
  if (!base) return null;
  return base + "/live.html?code=" + encodeURIComponent(code);
}

// Generate a QR-code image data URL (PNG) locally. No third-party service:
// works offline and never leaks the resume code to an external host.
async function qrCodeImageUrl(text, sizePx) {
  try {
    return await QRCode.toDataURL(String(text), {
      width: sizePx || 240,
      margin: 2,
      color: { dark: "#0f172a", light: "#ffffff" }
    });
  } catch (e) {
    console.warn("QR generation failed", e);
    return null;
  }
}

let _persistIdleHandle = null;
let _persistFallbackTimer = null;
let _persistQueued = false;

function _cloneActiveForStorage() {
  return cloneMatchForLocalSession(get(activeMatch));
}

function _validatedActiveSnapshot() {
  return prepareActiveSnapshot(_cloneActiveForStorage());
}

function _writeEmergencyActiveSnapshot(snapshot) {
  if (!snapshot) return false;
  // The synchronous emergency mirror only needs the newest score state, but
  // wiping the undo ring entirely meant that booting from this fallback (after
  // an IndexedDB quota failure) silently destroyed the user's ability to undo.
  // Keeping the last two entries costs a few KB and preserves single-step undo.
  const trimmed = Array.isArray(snapshot._undoStack) ? snapshot._undoStack.slice(-2) : [];
  try {
    localStorage.setItem(LS_ACTIVE, JSON.stringify({ ...snapshot, _undoStack: trimmed }));
    return true;
  } catch (error) {
    // Quota: retry once with no undo history, and if that still fails, trim logos.
    try {
      localStorage.setItem(LS_ACTIVE, JSON.stringify({ ...snapshot, _undoStack: [] }));
      return true;
    } catch (retryError) {
      const stripped = { ...snapshot, _undoStack: [] };
      if (stripped.teamA) stripped.teamA = { ...stripped.teamA, logo: null };
      if (stripped.teamB) stripped.teamB = { ...stripped.teamB, logo: null };
      try {
        localStorage.setItem(LS_ACTIVE, JSON.stringify(stripped));
        return true;
      } catch (e) {
        console.warn("Emergency active-match snapshot failed", e);
        return false;
      }
    }
  }
}

function _clearPersistSchedule() {
  if (_persistIdleHandle != null && window.cancelIdleCallback) {
    try { window.cancelIdleCallback(_persistIdleHandle); } catch (_) {}
  }
  if (_persistFallbackTimer != null) {
    clearTimeout(_persistFallbackTimer);
  }
  _persistIdleHandle = null;
  _persistFallbackTimer = null;
}

async function _writeActiveToStorageNow(prebuiltSnapshot) {
  const match = get(activeMatch);
  if (!match) {
    return;
  }
  const snapshot = prebuiltSnapshot !== undefined ? prebuiltSnapshot : _validatedActiveSnapshot();
  if (snapshot) {
    const db = await _getIDB();
    await idbPut(db, "active", snapshot, "current");
  } else {
    console.warn("persist skipped - match state invalid");
  }
}

async function _retryPersistAfterQuotaTrim() {
  Object.keys(teamMeta).forEach(k => { if (teamMeta[k]) teamMeta[k].logo = null; });
  try { localStorage.setItem(LS_TEAMS, JSON.stringify(teamMeta)); } catch (_) {}
  const match = get(activeMatch);
  if (match) {
    if (match.teamA) match.teamA.logo = null;
    if (match.teamB) match.teamB.logo = null;
    activeMatch.set(match);
  }
  try { await _writeActiveToStorageNow(); } catch (_) {}
}

// Snapshot built by the current persist() pass, reused by the deferred commit so
// the (expensive) clone+validate chain runs once per ball instead of twice.
let _queuedSnapshot = null;

async function _commitPersistQueue() {
  _clearPersistSchedule();
  if (!_persistQueued) return;
  _persistQueued = false;
  const snapshot = _queuedSnapshot;
  _queuedSnapshot = null;
  try {
    await _writeActiveToStorageNow(snapshot);
  } catch (e) {
    console.warn("persist failed (likely quota); trimming logos and retrying", e);
    await _retryPersistAfterQuotaTrim();
  }
}

function _schedulePersistCommit() {
  _persistQueued = true;
  if (_persistIdleHandle != null || _persistFallbackTimer != null) return;
  const run = () => { _commitPersistQueue(); };
  if (window.requestIdleCallback) {
    _persistIdleHandle = window.requestIdleCallback(run, { timeout: 450 });
  } else {
    _persistFallbackTimer = setTimeout(run, 140);
  }
}

function persist(opts) {
  opts = opts || {};
  try {
    const m = get(activeMatch);
    if (m && m._remoteControlled) {
      // Recorder-mode synthetic match: never overwrite the locally-saved match
      // and never cloud-sync it (the scorer phone owns the match data).
      return;
    }
    if (m) {
      m._stateRevision = (Number(m._stateRevision) || 0) + 1;
      m._savedAt = Date.now(); // wall-clock tiebreaker for cross-match staleness
      // Build the validated snapshot ONCE and share it with both writers.
      const snapshot = _validatedActiveSnapshot();
      _queuedSnapshot = snapshot;
      _writeEmergencyActiveSnapshot(snapshot);
      _getIDB()
        .then(db => writeRecoveryCheckpoint(db, m))
        .then(checkpoint => {
          if (!checkpoint) {
            // The write genuinely failed (quota / abort) — tell the user their
            // match is no longer protected instead of silently pretending.
            recoveryInfo.update(info => ({
              ...info,
              warning: "Storage is full — match recovery checkpoints are not being saved. Free up space."
            }));
          }
        })
        .catch(checkpointError => console.warn("recovery checkpoint failed", checkpointError));
      if (opts.immediate) {
        _persistQueued = true;
        _commitPersistQueue();
      } else {
        _schedulePersistCommit();
      }
      activeMatch.set(m); // Force Svelte reactivity for the mutated match
      // Best-effort auto cloud sync — non-blocking, debounced.
      scheduleAutoCloudSync();
      // Make sure heartbeat is running so even an idle match keeps a fresh
      // code on the cloud (no-op if already started).
      if (!_heartbeatSyncTimer) startHeartbeatSync();
      // Dual-phone mode: persist() runs after EVERY state change (runs, extras,
      // wickets, undo, new bowler/batsman, strike swap...), so this is the one
      // choke point that keeps the recorder phone perfectly in sync.
      if (opts.syncPeer !== false) setTimeout(sendCurrentMatchState, 0);
    } else {
      // No active match: just stop the persistence machinery. Stored data is
      // removed only by clearActiveMatch (an explicit user action) — never by
      // a passive flush, so a transient null can't destroy a saved match.
      _clearPersistSchedule();
      _persistQueued = false;
      stopHeartbeatSync();
    }
  } catch (e) {
    console.warn("persist failed (likely quota); trimming logos and retrying", e);
    _retryPersistAfterQuotaTrim(); // Can't await easily since persist isn't async
  }
}

async function clearActiveMatch(matchId = null) {
  _clearPersistSchedule();
  _persistQueued = false;
  activeMatch.set(null);
  localStorage.removeItem(LS_ACTIVE);
  const id = matchId || localStorage.getItem(LAST_RECOVERY_MATCH_KEY);
  const db = await _getIDB();
  await deleteActiveMatchRecords(db, id);
  if (!id || localStorage.getItem(LAST_RECOVERY_MATCH_KEY) === id) {
    localStorage.removeItem(LAST_RECOVERY_MATCH_KEY);
  }
  stopHeartbeatSync();
}

async function tryLockLandscape() {
  try {
    if (screen.orientation && screen.orientation.lock) {
      await screen.orientation.lock("landscape");
    }
  } catch (e) { /* not allowed unless fullscreen — ignore */ }
}

const IDB_NAME = "CricketScorerDB";
export function _getIDB() {
  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (fn, value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      fn(value);
    };
    // A blocked open (another tab holding an old connection) never fires
    // onsuccess/onerror — without a timeout the app's boot `await` hangs on a
    // blank screen forever. Fail instead; every caller falls back gracefully.
    const timer = setTimeout(() => finish(reject, new Error("IndexedDB open timed out (blocked?)")), 4000);
    let req;
    try {
      req = indexedDB.open(IDB_NAME, 2);
    } catch (e) {
      finish(reject, e);
      return;
    }
    req.onupgradeneeded = e => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains("matches")) db.createObjectStore("matches", { keyPath: "id" });
      if (!db.objectStoreNames.contains("active")) db.createObjectStore("active");
      if (!db.objectStoreNames.contains("checkpoints")) db.createObjectStore("checkpoints");
    };
    req.onsuccess = () => finish(resolve, req.result);
    req.onerror = () => finish(reject, req.error);
  });
}

// Write ONE match. Preferred over persistSaved(): clearing and rewriting the
// whole store meant saving the 51st match rewrote all 50 previous ones (~15 MB)
// to flash, freezing the UI and risking a transaction abort.
async function upsertSavedMatch(match) {
  if (!match || !match.id) return false;
  try {
    const db = await _getIDB();
    await idbPut(db, "matches", match); // keyPath: "id"
    return true;
  } catch (e) {
    console.error("IDB save error", e);
    return false;
  }
}

async function deleteSavedMatch(matchId) {
  if (!matchId) return false;
  try {
    const db = await _getIDB();
    await new Promise((resolve, reject) => {
      const tx = db.transaction("matches", "readwrite");
      tx.objectStore("matches").delete(matchId);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
    return true;
  } catch (e) {
    console.error("IDB delete error", e);
    return false;
  }
}

// Full rewrite. Only for bulk paths (one-time localStorage migration, bundle
// import) — never on the per-match save path.
function persistSaved() {
  _getIDB().then(db => {
    const tx = db.transaction("matches", "readwrite");
    const store = tx.objectStore("matches");
    store.clear();
    get(savedMatches).forEach(m => store.put(m));
  }).catch(e => console.error("IDB save error", e));
}

async function loadFromStorage() {
  let recoveryState = { available: false, restored: false, warning: "", latest: null, previous: null };
  try {
    const db = await _getIDB().catch(() => null);
    
    let indexedDbSnapshot = null;
    if (db) {
      try {
        indexedDbSnapshot = await new Promise(res => {
          const req = db.transaction("active", "readonly").objectStore("active").get("current");
          req.onsuccess = () => res(req.result);
          req.onerror = () => res(null);
        });
      } catch (_) {}
    }

    let emergencySnapshot = null;
    const emergencyJson = localStorage.getItem(LS_ACTIVE);
    if (emergencyJson) {
      try { emergencySnapshot = JSON.parse(emergencyJson); } catch (_) {}
    }
    const parsed = selectNewestActiveSnapshot(indexedDbSnapshot, emergencySnapshot);

    if (parsed) {
      const matchId = parsed?.id || localStorage.getItem(LAST_RECOVERY_MATCH_KEY);
      const normalizedParsed = normalizeSnapshot(parsed);
      const checkpoints = db ? await readRecoveryCheckpoints(db, matchId) : [];
      const selected = selectRecoveryState(normalizedParsed, checkpoints);
      if (selected.match) {
        activeMatch.set(selected.match);
        if (db) {
          db.transaction("active", "readwrite").objectStore("active").put(selected.match, "current");
        }
      }
      recoveryState = {
        available: !!selected.match,
        restored: selected.restored,
        warning: selected.warning,
        latest: selected.latest,
        previous: selected.previous
      };
    } else {
      // Both active snapshots (IndexedDB + localStorage mirror) are missing or
      // unreadable — exactly the disaster the recovery checkpoints exist for.
      // Restore the newest valid checkpoint of the last known match.
      const lastMatchId = localStorage.getItem(LAST_RECOVERY_MATCH_KEY);
      if (db && lastMatchId) {
        const checkpoints = await readRecoveryCheckpoints(db, lastMatchId);
        const selected = selectRecoveryState(null, checkpoints);
        if (selected.match) {
          activeMatch.set(selected.match);
          try {
            db.transaction("active", "readwrite").objectStore("active").put(selected.match, "current");
          } catch (_) {}
          recoveryState = {
            available: true,
            restored: true,
            warning: selected.warning || "Active match was missing. Restored the latest safe checkpoint.",
            latest: selected.latest,
            previous: selected.previous
          };
        }
      }
    }

    recoveryInfo.set(recoveryState);

    if (db) {
      const req2 = db.transaction("matches", "readonly").objectStore("matches").getAll();
      req2.onsuccess = () => {
        if (req2.result && req2.result.length > 0) {
          savedMatches.set(req2.result);
        } else {
          try {
            const s = localStorage.getItem(LS_SAVED);
            if (s) {
              savedMatches.set(JSON.parse(s));
              persistSaved(); // migrate
              // Finish the migration. Leaving the key behind meant exportAllData
              // kept shipping this frozen pre-migration copy instead of the
              // user's real (IndexedDB) history.
              localStorage.removeItem(LS_SAVED);
            }
          } catch(e){}
        }
      };
    }
  } catch (e) {
    console.error("Load active error", e);
  }
  return Promise.resolve();
}

async function restorePreviousCheckpoint(matchId) {
  const db = await _getIDB().catch(() => null);
  if (!db) return false;
  const checkpoints = await readRecoveryCheckpoints(db, matchId);
  const valid = checkpoints.filter(item => validateMatchSnapshot(normalizeSnapshot(item?.snapshot)).ok);
  const previous = valid.length > 1 ? valid.at(-2) : null;
  const restored = restoreCheckpoint(previous);
  if (!restored) return false;
  // Discard the checkpoints newer than the one being restored — otherwise the
  // next "restore previous ball" press would bounce back to the state the
  // user just rejected instead of stepping further back.
  const restoredIdx = checkpoints.indexOf(previous);
  if (restoredIdx >= 0 && restoredIdx < checkpoints.length - 1) {
    await writeRecoveryCheckpointList(db, matchId, checkpoints.slice(0, restoredIdx + 1));
  }
  activeMatch.set(restored);
  persist({ immediate: true });
  const latest = (await readRecoveryCheckpoints(db, matchId)).at(-1) || null;
  recoveryInfo.set({ available: true, restored: true, warning: "Restored the previous safe ball.", latest, previous: valid.length > 2 ? valid.at(-3) : null });
  return true;
}

function discardRecovery(matchId) {
  _getIDB().then(db => clearRecoveryCheckpoints(db, matchId)).catch(()=>{});
  if (localStorage.getItem(LAST_RECOVERY_MATCH_KEY) === matchId) localStorage.removeItem(LAST_RECOVERY_MATCH_KEY);
  recoveryInfo.set({ available: false, restored: false, warning: "", latest: null, previous: null });
}

async function enterFullscreen() {
  const root = document.documentElement;
  try {
    if (root.requestFullscreen) await root.requestFullscreen();
    else if (root.webkitRequestFullscreen) await root.webkitRequestFullscreen();
  } catch (e) { /* ignore */ }
}


// ─── EXPORT/IMPORT ALL (active match + scheduled matches) ───────────
// Bundles the active match AND scheduled matches into a single JSON for
// phone-to-phone transfer.
function _readLS(key, fallback) {
  try { return JSON.parse(localStorage.getItem(key) || fallback); } catch (_) { return JSON.parse(fallback); }
}

function exportAllData(activeMatchValue, scheduledMatches) {
  try {
    // v2 bundle: FULL phone-to-phone transfer — matches + teams + players.
    const bundle = {
      type: "ffpl_cricket_bundle",
      version: 2,
      exportedAt: new Date().toISOString(),
      activeMatch: activeMatchValue ? stripLocalDeviceSession(activeMatchValue) : null,
      scheduledMatches: Array.isArray(scheduledMatches) ? scheduledMatches : [],
      // Saved matches live in IndexedDB (the `savedMatches` store), NOT in
      // localStorage — reading LS_SAVED here exported an empty or long-stale
      // list and stranded the user's real match history on the old phone.
      savedMatches: get(savedMatches) || [],
      customTeams: _readLS("cs_custom_teams_v1", "[]"),
      teamMeta: _readLS(LS_TEAMS, "{}"),
      playerRegistry: _readLS("cs_player_registry", "{}")
    };
    _downloadJson(`ffpl_cricket_all_${_exportStamp()}.json`, bundle);
  } catch (e) {
    toast("Export failed: " + (e.message || e), "error");
  }
}

function importAllData(onDone) {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "application/json,.json";
  input.onchange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const updated = { match: false, scheduled: false };

      if (data && data.type === "ffpl_cricket_bundle") {
        // --- Bundle format ---
        // Import active match if present
        if (data.activeMatch) {
          const checked = normalizeImportedMatch(data.activeMatch);
          if (checked.ok) {
            if (get(activeMatch) && !(await confirmDialog("This will replace the current active match. Continue?", { title: "Import all", danger: true }))) return;
            activeMatch.set(checked.value);
            persist();
            updated.match = true;
          }
        }
        // Import scheduled matches
        if (Array.isArray(data.scheduledMatches) && data.scheduledMatches.length > 0) {
          const existing = JSON.parse(localStorage.getItem("cs_scheduled_v1") || "[]");
          const existingIds = new Set(existing.map(m => m.id));
          const newScheduled = data.scheduledMatches.filter(m => m && m.id && !existingIds.has(m.id));
          if (newScheduled.length > 0) {
            const merged = [...existing, ...newScheduled];
            localStorage.setItem("cs_scheduled_v1", JSON.stringify(merged));
            updated.scheduled = true;
          }
        }

        // --- v2 additions: saved matches + teams + players (non-destructive merge) ---
        if (Array.isArray(data.savedMatches) && data.savedMatches.length > 0) {
          const current = get(savedMatches) || [];
          const currentIds = new Set(current.map(m => m?.id));
          const incoming = data.savedMatches.filter(m => {
            if (!m?.id || currentIds.has(m.id)) return false;
            return normalizeImportedMatch(m).ok;
          }).map(m => normalizeImportedMatch(m).value);
          if (incoming.length > 0) {
            savedMatches.set([...current, ...incoming]);
            persistSaved();
            updated.saved = incoming.length;
          }
        }
        if (Array.isArray(data.customTeams) && data.customTeams.length > 0) {
          const existing = _readLS("cs_custom_teams_v1", "[]");
          const names = new Set(existing.map(t => t?.name));
          const fresh = data.customTeams.filter(t => t?.name && !names.has(t.name));
          if (fresh.length > 0) {
            localStorage.setItem("cs_custom_teams_v1", JSON.stringify([...existing, ...fresh]));
            updated.teams = fresh.length;
          }
        }
        if (data.teamMeta && typeof data.teamMeta === 'object') {
          // Incoming colors/logos fill gaps; local customizations win.
          teamMeta = { ...data.teamMeta, ...teamMeta };
          saveTeamMeta();
        }
        if (data.playerRegistry && typeof data.playerRegistry === 'object' && Object.keys(data.playerRegistry).length > 0) {
          const reg = _readLS("cs_player_registry", "{}");
          let added = 0;
          for (const [key, value] of Object.entries(data.playerRegistry)) {
            if (!reg[key]) { reg[key] = value; added++; }
          }
          if (added > 0) {
            localStorage.setItem("cs_player_registry", JSON.stringify(reg));
            updated.players = added;
          }
        }

        const parts = [];
        if (updated.match) parts.push("active match restored");
        if (updated.scheduled) parts.push("scheduled matches imported");
        if (updated.saved) parts.push(`${updated.saved} saved match(es)`);
        if (updated.teams) parts.push(`${updated.teams} team(s)`);
        if (updated.players) parts.push(`${updated.players} player profile(s)`);
        if (parts.length > 0) {
          toast("✅ Import done: " + parts.join(" + ") + ".", "success");
        } else {
          toast("Nothing new to import — data already exists or was empty.");
        }
      } else {
        // --- Single match format (backward compatible) ---
        const checked = normalizeImportedMatch(data);
        if (!checked.ok) {
          await alertDialog("That file doesn't look like a Cricket Scorer file: " + checked.error, { title: "Import failed" });
          return;
        }
        if (get(activeMatch) && !(await confirmDialog("This will replace the current active match. Continue?", { title: "Import match", danger: true }))) return;
        activeMatch.set(checked.value);
        persist();
        updated.match = true;
        toast("✅ Match imported — you can now resume scoring.", "success");
      }
      if (typeof onDone === "function") onDone(updated);
    } catch (err) {
      toast("Couldn't read the file: " + (err.message || err), "error");
    }
  };
  input.click();
}

// ═══════════════════════════════════════════════════════════════════════
// EXPORTS — everything that other modules need
// ═══════════════════════════════════════════════════════════════════════
export {
  LS_SETTINGS, SETTINGS_SCHEMA_VERSION, DEFAULT_SETTINGS,
  RESOLUTION_PRESETS, RESOLUTION_FALLBACK_ORDER,
  getResolutionPreset, appSettings, saveSettings,
  LS_ACTIVE, LS_SAVED, LS_TEAMS,
  teamMeta, saveTeamMeta, getMeta, setMeta,
  resizeImageDataUrl,
  exportMatchToFile, importMatchFromFile,
  exportAllData, importAllData,
  getCloudSyncEndpoint, cloudSyncMatch, cloudResumeMatch,
  scheduleAutoCloudSync, startHeartbeatSync, stopHeartbeatSync,
  buildResumeUrl, buildLiveUrl, qrCodeImageUrl,
  persist, persistSaved, upsertSavedMatch, deleteSavedMatch,
  loadFromStorage, restorePreviousCheckpoint, discardRecovery, clearActiveMatch,
  tryLockLandscape, enterFullscreen
};
